// Content for the 5 SEO landing pages (copy supplied by Jess, used verbatim).
// Inline **bold** is rendered by LandingBlocks. Block types: h2, h3, p, stats, note, list.
const h2 = (text) => ({ type: 'h2', text });
const h3 = (text) => ({ type: 'h3', text });
const p = (text) => ({ type: 'p', text });
const note = (text) => ({ type: 'note', text });
const list = (items) => ({ type: 'list', items });
const stats = (items) => ({ type: 'stats', items });

export const LANDING_PAGES = [
  {
    slug: 'pcsing-to-nas-oceana-norfolk',
    kind: 'guide',
    menu: 'buyers',
    footerLabel: 'PCS Housing Guide: NAS Oceana & Norfolk',
    title: 'PCSing to NAS Oceana or Naval Station Norfolk: The Ultimate Housing Guide | Jess Sells East Coast',
    description: "PCS orders to Hampton Roads? Military Relocation Professional Jessica Burnette's complete housing guide for NAS Oceana, Naval Station Norfolk, and every base in between.",
    eyebrow: 'Your PCS Playbook',
    h1: 'PCSing to NAS Oceana or Naval Station Norfolk: The Ultimate Housing Guide',
    intro: "Got orders to Hampton Roads? I'm a certified Military Relocation Professional. Here's everything I wish someone had told me before my move, from someone who now sells homes here every week.",
    sections: [
      [
        h2('Hampton Roads Is Military Country'),
        p("This region has 15 military installations and more than 80,000 active duty service members. Virginia holds 26% of all active duty Navy personnel, more than any other state. If you got orders here, you're joining the biggest military community in the country."),
        stats([
          { num: '15', label: 'Military installations across Hampton Roads' },
          { num: '80,000+', label: 'Active duty service members in the region' },
          { num: '#1', label: "Naval Station Norfolk: world's largest naval station" },
        ]),
        h3('NAS Oceana (Virginia Beach)'),
        p("The Navy's East Coast Master Jet Base, home to the F/A-18 Super Hornet squadrons. About 10,500 active duty sailors plus roughly 10,000 family members and 4,500 civilians work here (Navy CNIC figures). It's the second largest employer in Virginia Beach."),
        h3('Naval Station Norfolk (Norfolk)'),
        p("The world's largest naval station, sitting on the Sewells Point peninsula. Around 46,000 active duty and 21,000 civilians work on the installation (Hampton Roads Chamber figures). If your ship is homeported here, this is your base."),
        h3('Other Major Commands'),
        p("Joint Expeditionary Base Little Creek-Fort Story (Virginia Beach), Norfolk Naval Shipyard (Portsmouth), Joint Base Langley-Eustis (Hampton/Newport News), and Coast Guard Base Portsmouth. Tell me your command and I'll tell you exactly which neighborhoods make sense."),
      ],
      [
        h2('Where Military Families Actually Live'),
        h3('Near NAS Oceana'),
        p('The most popular areas for Oceana families: **Salem, Red Mill, Kempsville, and Lynnhaven** in Virginia Beach. Neighborhoods in the 23456 zip like Castleton and Lago Mar are favorites because the commute skips the tunnels entirely. Families attached to Dam Neck Annex often look toward Sandbridge and Pungo for a quieter feel.'),
        h3('Near Naval Station Norfolk'),
        p('On the Norfolk side, military families cluster in **Larchmont, Ghent, Colonial Place, East Beach, and Ocean View**. A lot of Norfolk-based families also buy in Chesapeake or Virginia Beach and commute about 30 minutes. That trade works if you want newer construction or specific school zones.'),
        note("**The tunnel talk:** I-64 is the main corridor and the Hampton Roads Bridge-Tunnel and Monitor-Merrimac Tunnel are the region's notorious bottlenecks. Where you live relative to the water genuinely shapes your daily life here. This is the number one thing to get right, and it's exactly what I walk my PCS buyers through first."),
        h3("Watch-Outs I'll Flag For You"),
        list([
          "**Aircraft noise** near Oceana flight paths. Some streets hear the jets, some don't. I'll tell you which is which.",
          '**Flood zones** in low lying and tidal areas. Flood insurance is a real budget line here, not an afterthought.',
          "**Summer tourist traffic** around the Virginia Beach oceanfront. Great if you're buying a second home, worth knowing if you're commuting through it.",
        ]),
      ],
      [
        h2('Rent vs. Buy on a PCS Move'),
        p("Here's my honest take as someone who's been the one unpacking boxes: if your orders are 3+ years and the numbers work, buying in Hampton Roads has built real wealth for a lot of military families. The VA loan with zero down means you can buy without draining savings, and this market has steady military-driven demand behind it."),
        p("If your orders are under 2 years or you're unsure about the area, renting first is the smart play. There's no shame in it. I'd rather you rent for a year and buy right than buy fast and regret it."),
        h3('Your PCS Buying Timeline'),
        list([
          '**As soon as you have orders:** call me. We talk neighborhoods, budget, and timeline. I send listings before you ever get on a plane.',
          '**60 to 90 days out:** get pre-approved with a VA-savvy lender. I have names if you need one.',
          '**30 days out:** house hunting trip or virtual tours. I walk homes on video for my out of state buyers all the time.',
          '**After closing:** I stay your resource. Contractors, schools, the best taco truck near base, all of it.',
        ]),
        note("PCS orders don't wait for the perfect market timing. What matters is buying the right house in the right spot at a payment that works. That's a conversation, not a calculator. Let's have it."),
      ],
    ],
    closing: {
      eyebrow: 'Ready When You Are',
      h2: "Orders in Hand? Let's Talk Before You House Hunt.",
      text: "Tell me your command and your report date. I'll send you neighborhoods that fit your commute, your budget, and your life.",
      cta: 'call',
    },
  },
  {
    slug: 'va-loan-guide',
    kind: 'guide',
    menu: 'buyers',
    footerLabel: 'VA Loan Guide (2026)',
    title: 'How to Use a VA Loan in Coastal VA & NC (2026 Guide) | Jess Sells East Coast',
    description: '2026 VA loan guide for Hampton Roads VA and the Outer Banks NC: zero down limits, funding fees, no PMI, plus Virginia and North Carolina veteran homebuyer programs.',
    eyebrow: 'VA Loan, Plain English',
    h1: 'How to Use a VA Loan in Coastal VA & NC',
    intro: "The VA loan is the best mortgage in America. Zero down, no PMI, competitive rates. Here's exactly how it works for buying in Hampton Roads and the Outer Banks, in plain English.",
    sections: [
      [
        h2('The Headline: $0 Down, No Limit'),
        p('If you have full VA entitlement, there is **no loan limit** in 2026. You can buy at $400,000 or $1.4 million with zero down, as long as the lender approves the payment. That changed with the Blue Water Navy Act and it is still the rule today.'),
        p('If you have partial entitlement (an active VA loan or a past VA loan loss), the zero-down cap follows the county limit, which is **$832,750** for 2026 in our markets: Virginia Beach, Norfolk, Chesapeake, Currituck County, and Dare County are all standard-limit counties.'),
        stats([
          { num: '$0', label: 'Down payment with full entitlement, no loan limit' },
          { num: '$832,750', label: '2026 zero-down cap with partial entitlement (our counties)' },
          { num: '$0', label: 'Private mortgage insurance. Ever.' },
        ]),
      ],
      [
        h2('The VA Funding Fee (2026 Rates)'),
        p('The funding fee keeps the program running. It can be rolled into the loan, so most buyers never write a check for it. Current rates:'),
        list([
          '**Less than 5% down:** 2.15% first use, 3.30% each use after',
          '**5 to 10% down:** 1.50% first or subsequent use',
          '**10%+ down:** 1.25% first or subsequent use',
        ]),
        note('**Exempt from the fee entirely:** veterans receiving VA disability compensation, active duty Purple Heart recipients, and eligible surviving spouses. If any of these is you, your funding fee is zero.'),
        h3('What Else Makes VA Loans Different'),
        list([
          "**No PMI, ever.** Conventional buyers with less than 20% down pay monthly PMI. You don't.",
          '**VA appraisal required.** A VA-assigned appraiser checks the home meets Minimum Property Requirements. It protects you from buying a problem.',
          '**Primary residence.** The home has to be your primary residence, generally occupied within 60 days of closing.',
          "**Seller concessions.** Sellers can pay all your closing costs plus up to 4% in concessions. In negotiation, that's leverage.",
        ]),
      ],
      [
        h2('State Programs That Stack With Your VA Loan'),
        h3('Virginia: $5,000 Community Heroes Grant'),
        p('Virginia Housing offers a **$5,000 gift** (not a loan, a gift) for eligible first time buyers including active duty military and veterans. It goes toward down payment or closing costs. Free money on top of zero down.'),
        h3('North Carolina: $15,000 for Military Veterans'),
        p("The NC Housing Finance Agency's 1st Home Advantage program offers **$15,000 in down payment assistance** for eligible military veterans, and it can be combined with a VA loan. It's structured as a deferred second mortgage that's forgiven over time."),
        note('Programs change and have income, credit, and eligibility rules. I check current guidelines with my lender partners before we write offers, so what we plan around is real, not hopeful.'),
        h3('Mistakes I See Military Buyers Make'),
        list([
          '**Waiting to call an agent until after pre-approval.** Call both at once. I catch neighborhood and commute issues lenders never see.',
          '**Assuming BAH equals budget.** Taxes, insurance, flood zones, and HOA dues all live inside that payment. We run the full picture.',
          '**Skipping the funding fee math.** On a second VA use with zero down, 3.30% is real money. Sometimes a small down payment saves thousands. We do that math together.',
          '**Buying near base without checking noise and flood maps.** I check both before you fall in love with a house.',
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Free, No Pressure',
      h2: "Let's Run Your VA Numbers.",
      text: "Tell me your situation and I'll connect you with a VA-savvy lender and start sending homes that fit. No pressure, just a plan.",
      cta: 'call',
    },
  },
  {
    slug: 'sandbridge',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: 'Sandbridge Spotlight',
    heroImage: '/img/spotlights/sandbridge.webp',
    heroAlt: 'Sand dunes, sea oats and beach houses at Sandbridge Beach, Virginia Beach',
    title: 'Sandbridge VA Neighborhood Spotlight | Jess Sells East Coast',
    description: 'Sandbridge, Virginia Beach: quiet oceanfront living, market stats, price ranges, and what to know before buying. Free home valuations from Jess Sells East Coast.',
    eyebrow: 'Neighborhood Spotlight',
    h1: 'Sandbridge, Virginia Beach',
    intro: 'The quiet end of the oceanfront. About four and a half miles of beach, one road in, and some of the most peaceful waterfront living in Virginia Beach.',
    sections: [
      [
        h2('The Vibe'),
        p("Sandbridge sits on the Currituck Banks Peninsula in the 23456 zip, with the Atlantic Ocean on one side and Back Bay on the other. South of the neighborhoods you'll find the Back Bay National Wildlife Refuge (about 9,100 acres) and False Cape State Park beyond it, reachable only on foot, bike, or boat. Dam Neck naval facility anchors the north end."),
        p("It's mostly single family beach homes with some condos at each end, and a heavy mix of second homes and vacation properties. If the resort strip feels too busy, Sandbridge is the exhale."),
        stats([
          { num: '~$972K', label: 'Median list price, mid-2026 (third party estimate)' },
          { num: '~$400', label: 'Price per square foot, approximate' },
          { num: '~60', label: 'Median days on market' },
        ]),
        note("Market figures are approximate third party estimates from mid-2026, not MLS verified. For your street's real number, get a valuation. That's free and it's what I do."),
        h3('Who Buys in Sandbridge'),
        p('Second home buyers who want the beach without the boardwalk. Remote workers who traded a commute for an ocean view. Investors running vacation rentals. And locals who grew up coming here and never wanted to leave.'),
        h3('What to Know Before You Buy Here'),
        list([
          '**One road in and out.** Sandbridge Road is the only public entrance. In summer, plan around beach traffic.',
          "**Flood insurance is part of the budget.** You're between the ocean and the bay. We price it in before you write an offer, not after.",
          "**Vacation rental rules matter.** If you're buying as an investment, we verify the rental math and the current city rules together.",
          "**It's about 15 miles south of the resort area.** Peaceful is the point, but know your drive to work, to base, to everything.",
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Thinking of Selling?',
      h2: "What's Your Sandbridge Home Worth?",
      text: 'Beach markets move on their own logic. Get a free valuation backed by real local data, not a computer guess.',
      cta: 'valuation',
    },
  },
  {
    slug: 'chicks-beach',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: "Chick's Beach Spotlight",
    heroImage: '/img/spotlights/chicks-beach.webp',
    heroAlt: "Sunset over the Chesapeake Bay at Chick's Beach, Virginia Beach",
    title: "Chick's Beach VA Neighborhood Spotlight | Jess Sells East Coast",
    description: "Chick's Beach, Virginia Beach: Chesapeake Bay waterfront, market stats, price bands from condos to bayfront estates. Free home valuations from Jess Sells East Coast.",
    eyebrow: 'Neighborhood Spotlight',
    h1: "Chick's Beach, Virginia Beach",
    intro: "Bayfront living on the Chesapeake, calm water, golf cart streets, and waterfront you can actually afford. This is the locals' beach.",
    sections: [
      [
        h2('The Vibe'),
        p("Chick's Beach (locals also write it Chic's Beach) is about two miles of Chesapeake Bay shoreline running from the Lynnhaven Inlet to the Chesapeake Bay Bridge-Tunnel, bordered by Shore Drive. Zips 23455 and 23451. The water is calm and kid friendly, the streets are golf cart friendly, and the dining corridor along Shore Drive (think Chick's Oyster Bar and Buoy 44) is pure local flavor."),
        p('The housing mix is the draw: beach cottages, condos, townhouses, duplexes, and single family homes, all the way up to bayfront estates with private docks. First Landing State Park and the Pleasure House Point Natural Area are minutes away.'),
        stats([
          { num: '~$594K', label: 'Median sale price, last 12 months (third party)' },
          { num: '~$390', label: 'Price per square foot, approximate' },
          { num: '~40', label: 'Median days on market' },
        ]),
        h3('The Price Bands'),
        list([
          '**Condos and 2-bedrooms:** roughly $350K to $600K',
          '**Duplexes and townhomes:** roughly $600K to $900K',
          '**Single family homes:** roughly $800K to $1.2M',
          '**Bayfront estates with private docks:** $1.5M and up',
        ]),
        note("Bands and medians are approximate third party figures from 2026. Your home's value depends on water frontage, views, condition, and updates. I run that number for free."),
        h3("Who Buys at Chick's Beach"),
        p('Military families from nearby Little Creek who want water without the oceanfront premium. Boaters who need a dock more than a beach. First time waterfront buyers who thought waterfront was out of reach. And longtime locals who would never live anywhere else.'),
        h3('What to Know Before You Buy Here'),
        list([
          "**Bay vs. ocean is a lifestyle choice.** Calm water, sunsets over the bay, no big surf. Make sure that's your speed.",
          '**Flood zones vary street by street.** Two blocks can mean two different insurance bills. I check the maps on every listing.',
          '**Shore Drive is evolving.** New development keeps coming to the corridor, which is good for values and worth watching for traffic.',
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Thinking of Selling?',
      h2: "What's Your Chick's Beach Home Worth?",
      text: 'Waterfront values are hyper local. A free valuation from someone who sells here beats any automated estimate.',
      cta: 'valuation',
    },
  },
  {
    slug: 'corolla',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: 'Corolla Spotlight',
    heroImage: '/img/spotlights/corolla.webp',
    heroAlt: 'Wild Colonial Spanish Mustangs on the beach at Corolla, Outer Banks NC',
    title: 'Corolla NC Neighborhood Spotlight | Jess Sells East Coast',
    description: 'Corolla, Outer Banks NC: wild horses, 4x4 beaches, luxury vacation homes. Market stats and buying guidance from Jess Sells East Coast.',
    eyebrow: 'Neighborhood Spotlight',
    h1: 'Corolla, Outer Banks NC',
    intro: 'Where the Outer Banks gets quiet and luxurious. Wild horses on the beach, 4x4 sand roads to the north, and some of the finest vacation homes on the East Coast.',
    sections: [
      [
        h2('The Vibe'),
        p('Corolla is the northernmost town on the Outer Banks, in Currituck County, NC. Highway 12 ends here. North of town, the pavement ends too, and the 4x4 beaches of Carova begin, where the wild Colonial Spanish Mustangs roam free.'),
        p("This is an upscale second home and vacation rental market. The homes are big, many built specifically as weekly vacation rentals, with pools, theaters, and ocean views. It's quieter than Kitty Hawk or Nags Head, and deliberately so."),
        stats([
          { num: '$889K', label: 'Median sale price YTD Aug 2026 (Outer Banks Assoc. of Realtors)' },
          { num: '+23%', label: 'Sales growth year over year' },
          { num: '~80-97', label: 'Average days on market' },
        ]),
        note('Corolla figures from the Outer Banks Association of Realtors August 2026 market report. The wider OBX median sits around $615K, so Corolla commands a real premium.'),
        h3('Who Buys in Corolla'),
        p('Second home buyers from up and down the East Coast. Investors buying weekly vacation rentals (this is one of the strongest rental markets in the OBX). And a few lucky full timers who work remotely and wanted the beach as a backyard.'),
        h3('What to Know Before You Buy Here'),
        list([
          "**Rental history matters.** If you're buying an investment, we look at actual rental income, not projections. I know what to ask for.",
          "**It's the far end of the Banks.** Gorgeous and peaceful, but it's a drive to everything south. Know your tolerance.",
          "**Storm and flood exposure is real.** Insurance, elevation, and construction quality aren't fine print here. They're the purchase.",
          "**I'm licensed in North Carolina too.** Most Virginia Beach agents can't sell you a Corolla home. I can, on both sides of the line.",
        ]),
      ],
    ],
    closing: {
      eyebrow: "Let's Talk It Through",
      h2: 'Thinking About Corolla?',
      text: "Whether it's a second home or a vacation rental investment, let's talk numbers before you fall in love with a view.",
      cta: 'call',
    },
  },
  {
    slug: 'duck',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: 'Duck Spotlight',
    heroImage: '/img/spotlights/duck.webp',
    heroAlt: 'Soundside boardwalk over the marsh at sunset in Duck, Outer Banks NC',
    title: 'Duck NC Neighborhood Spotlight | Jess Sells East Coast',
    description: 'Duck, Outer Banks NC: boutique village, soundside boardwalk, premium oceanfront and soundfront homes. Market stats and buying guidance from Jess Sells East Coast.',
    eyebrow: 'Neighborhood Spotlight',
    h1: 'Duck, Outer Banks NC',
    intro: "The Outer Banks' boutique village. A soundside boardwalk, independent shops and restaurants, and some of the most sought-after oceanfront and soundfront homes on the northern beaches.",
    sections: [
      [
        h2('The Vibe'),
        p("Duck sits on a narrow stretch of the northern Outer Banks between Southern Shores and Corolla, with the Atlantic on one side and the Currituck Sound on the other. The town's soundside boardwalk links Duck Town Park to waterfront shops and restaurants, and the whole village is built for walking and biking. Beach access is reserved for Duck's residents and their guests, which keeps the sand uncrowded even in July."),
        p("Homes range from classic soundside cottages to large oceanfront rental estates with pools, elevators and ocean-to-sound views. It's quieter than Nags Head or Kill Devil Hills and more walkable than Corolla."),
        stats([
          { num: '~$1M', label: 'Median sale price, detached homes, 2026 YTD (third party, OBAR data)' },
          { num: '~60', label: 'Average days on market, northern OBX, summer 2026 (third party)' },
          { num: '~1 mi', label: 'Soundside boardwalk through the village' },
        ]),
        note("Duck figures are approximate third party estimates based on Outer Banks Association of Realtors data through mid-2026. Medians swing with the mix of homes sold; your home's number depends on frontage, views and rental history. I run that for free."),
        h3('Who Buys in Duck'),
        p("Second home buyers who want a walkable village instead of a car-first beach town. Vacation rental investors who know Duck's name sells itself in a listing title. Boaters and paddlers on the soundside. And retirees who found their forever beach."),
        h3('What to Know Before You Buy Here'),
        list([
          "**Beach access is for residents and guests.** That's a feature for owners and renters, but confirm how your specific street gets to the sand.",
          '**Rental income varies by location.** Oceanfront and ocean-to-sound homes rent for a premium; soundside cottages rent steadily but differently. We look at real rental history.',
          '**Highway 12 is the only road.** Summer Saturdays are check-in day traffic. Know it before you commit to a Saturday turnover schedule.',
          '**Flood zones and elevation matter.** Soundside and oceanfront carry different insurance pictures. I check both before you write.',
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Thinking of Selling?',
      h2: "What's Your Duck Home Worth?",
      text: 'Duck values move with the rental calendar and the mix of homes on the market. Get a free valuation from someone licensed on both sides of the state line.',
      cta: 'valuation',
    },
  },
  {
    slug: 'nags-head',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: 'Nags Head Spotlight',
    heroImage: '/img/spotlights/nags-head.webp',
    heroAlt: "Wind-rippled sand dunes at Jockey's Ridge State Park, Nags Head NC",
    title: 'Nags Head NC Neighborhood Spotlight | Jess Sells East Coast',
    description: "Nags Head, Outer Banks NC: Jockey's Ridge, fishing piers, historic beach cottages and rental-income homes. Market stats and buying guidance from Jess Sells East Coast.",
    eyebrow: 'Neighborhood Spotlight',
    h1: 'Nags Head, Outer Banks NC',
    intro: 'The heart of the Outer Banks. The tallest sand dunes on the East Coast, three fishing piers, the famous unpainted cottages along Beach Road, and a market built on year-round rental demand.',
    sections: [
      [
        h2('The Vibe'),
        p("Nags Head runs along about 11 miles of the central Outer Banks, from Kill Devil Hills down to Whalebone Junction where Highway 12 turns toward Hatteras. Jockey's Ridge State Park is the landmark, with a dune field rising 80 to 100 feet. The Nags Head Fishing Pier, Jennette's Pier and the Outer Banks Fishing Pier anchor the oceanfront, and the historic cottages along Virginia Dare Trail are the 'unpainted aristocracy' you've seen in photos."),
        p("Housing is a mix: oceanfront and oceanside rental homes, soundside neighborhoods like Nags Head Acres and the Village at Nags Head with its golf course, and the quieter cottages of South Nags Head below Whalebone. It's the most central, most convenient address on the beach."),
        stats([
          { num: '~$870K', label: 'Median sale price, Q3 2026 (third party)' },
          { num: '~64', label: 'Average days on market, Q3 2026 (third party)' },
          { num: '~11 mi', label: 'Of beach from Kill Devil Hills to Whalebone Junction' },
        ]),
        note("Nags Head figures are approximate third party estimates for 2026 and vary by source and zip. Listing medians run lower than sale medians because of the condo and soundside mix. For your street's real number, get a valuation. It's free."),
        h3('Who Buys in Nags Head'),
        p('Vacation rental investors who want the most central address on the Banks. Second home buyers who like having restaurants, groceries and the piers within a few minutes. Fishing and watersports families. And full time locals who work in Manteo or Kill Devil Hills.'),
        h3('What to Know Before You Buy Here'),
        list([
          '**Oceanfront erosion and beach nourishment.** Nags Head has invested in nourishment; still, ask about the history on any oceanfront lot.',
          '**Soundside vs. oceanside is a different budget.** Both have strong demand; the price gap funds a lot of upgrades. We talk about which fits your goals.',
          '**South Nags Head is its own world.** Quieter, no commercial strip, and the Cape Hatteras National Seashore just past the town line. Know the drive.',
          "**Rental history over projections.** If you're buying as an investment, we pull actual numbers, not the brochure.",
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Thinking of Selling?',
      h2: "What's Your Nags Head Home Worth?",
      text: 'Rental season, pier proximity and oceanfront exposure all move the number. Get a free valuation from a REALTOR® licensed in North Carolina and Virginia.',
      cta: 'valuation',
    },
  },
  {
    slug: 'great-neck',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: 'Great Neck Spotlight',
    heroImage: '/img/spotlights/great-neck.webp',
    heroAlt: 'Private dock and boat on the Lynnhaven River in Great Neck, Virginia Beach',
    title: 'Great Neck Virginia Beach Neighborhood Spotlight | Jess Sells East Coast',
    description: 'Great Neck, Virginia Beach: Lynnhaven River and Broad Bay waterfront, top-rated Cox High School zone, established neighborhoods. Market stats and free home valuations from Jess Sells East Coast.',
    eyebrow: 'Neighborhood Spotlight',
    h1: 'Great Neck, Virginia Beach',
    intro: 'Where Virginia Beach keeps its deepwater docks and its most sought-after school zone. Established neighborhoods along the Lynnhaven River and Broad Bay, minutes from First Landing State Park, Hilltop and the North End beaches.',
    sections: [
      [
        h2('The Vibe'),
        p("Great Neck is the peninsula north of Virginia Beach Boulevard, running up Great Neck Road to Shore Drive, with the Lynnhaven River to the west and Broad Bay and Linkhorn Bay to the east. Neighborhoods like Great Neck Estates, Great Neck Point, Broad Bay Point Greens, Wolfsnare Plantation and Chelsea were mostly built out decades ago, so there's little new construction and a lot of mature trees."),
        p('Most of the peninsula is zoned for Frank W. Cox High School and Great Neck Middle, two of the most sought-after schools in the city, though zoning changes by address so we always confirm. The housing runs from brick ranches and colonials on big lots to deepwater estates with private docks and boat lifts.'),
        stats([
          { num: '~$685K', label: 'Median home price, Cox High School zone, 2026 (third party)' },
          { num: '~21', label: 'Median days on market, 2026 (third party)' },
          { num: '$1M+', label: 'Typical entry for Lynnhaven River or Broad Bay waterfront' },
        ]),
        note("Great Neck medians vary widely by source because the corridor mixes condos, townhomes and waterfront estates. Figures are approximate third party estimates from 2026. Your home's number is about your street, your lot and your water. I run it for free."),
        h3('Who Buys in Great Neck'),
        p('Families moving for the Cox and Great Neck school zone. Boaters who want a dock with deep water and a short run to the Lynnhaven Inlet. Move-up buyers from Kempsville and the Oceanfront who want bigger lots. And longtime owners trading one Great Neck street for another.'),
        h3('What to Know Before You Buy Here'),
        list([
          '**School zoning is by address, not by neighborhood.** Two streets apart can mean two different high schools. I verify before you fall in love.',
          "**Waterfront means homework.** Bulkheads, docks, riparian rights and flood insurance all need checking. I've done it before, many times.",
          '**Little new construction.** That keeps supply tight and values steady, but it means most homes are 30 to 60 years old. Inspections matter.',
          '**Traffic on Great Neck Road.** One main spine for the whole peninsula. Know your commute timing before you choose a side.',
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Thinking of Selling?',
      h2: "What's Your Great Neck Home Worth?",
      text: 'Lot size, water frontage and school zone move Great Neck values more than any online estimate can measure. Get a free, local valuation.',
      cta: 'valuation',
    },
  },
  {
    slug: 'north-end',
    kind: 'neighborhood',
    menu: 'sellers',
    footerLabel: 'North End Spotlight',
    heroImage: '/img/spotlights/north-end.webp',
    heroAlt: 'Cedar-shake beach cottages and dunes on a quiet North End street, Virginia Beach',
    title: 'North End Virginia Beach Neighborhood Spotlight | Jess Sells East Coast',
    description: 'North End, Virginia Beach: oceanfront and oceanside beach cottages from 42nd to 89th Street, bike-friendly streets, no boardwalk crowds. Market stats and free home valuations from Jess Sells East Coast.',
    eyebrow: 'Neighborhood Spotlight',
    h1: 'The North End, Virginia Beach',
    intro: "Virginia Beach's quiet beach address. Oceanfront and oceanside homes from 42nd Street to 89th Street, no high-rises, no boardwalk, and a bike ride to the Cavalier and First Landing State Park.",
    sections: [
      [
        h2('The Vibe'),
        p('The North End starts where the resort strip ends, around 42nd Street, and runs north to 89th Street at the edge of Fort Story. Numbered streets run from Atlantic Avenue straight to the beach, so nearly every home is a short walk to the sand. There is no commercial strip, no hotels and no boardwalk crowds, just dunes, beach cottages and a lot of bicycles. The historic Cavalier Hotel sits at the southern end and First Landing State Park is at the northern end.'),
        p("Housing is oceanfront and oceanside on the east side of Atlantic Avenue, and the 'bayside' streets west of Atlantic reach toward Linkhorn Bay and Crystal Lake. Cavalier Shores, Ubermeer and Princess Anne Hills are the names you'll hear. Expect classic cedar-shake cottages next to brand new custom builds."),
        stats([
          { num: '~$1.1M', label: 'Median sale price, mid-2026 (third party)' },
          { num: '$1.8M+', label: 'Typical entry for true oceanfront, 2026 (third party)' },
          { num: '42nd–89th', label: 'The streets that make up the North End' },
        ]),
        note("North End figures are approximate third party estimates from 2026. Oceanfront, second row and bayside are three different markets inside one neighborhood, so medians only tell part of the story. For your street's real number, get a valuation."),
        h3('Who Buys at the North End'),
        p('Second home buyers who want the beach without the resort scene. Families who want to bike to the sand. Military officers from Fort Story and JEB Little Creek. And buyers trading a Great Neck or Alanton waterfront for an ocean view.'),
        h3('What to Know Before You Buy Here'),
        list([
          "**Oceanfront, second row, bayside.** Three price tiers, three insurance pictures. Decide which trade you're making before we tour.",
          '**Flood zones and dune lines.** Most of the North End is low and close to water. Elevation certificates and flood history come with every showing.',
          "**Summer parking and beach traffic.** Residents-only parking programs help, but Atlantic Avenue is busy in season. It's the price of the address.",
          "**Lot value drives teardowns.** Many sales here are land plays for new custom builds. If you're selling an original cottage, your lot may be worth more than your house.",
        ]),
      ],
    ],
    closing: {
      eyebrow: 'Thinking of Selling?',
      h2: "What's Your North End Home Worth?",
      text: 'Between teardown lot values and oceanfront premiums, North End pricing is anything but automated. Get a free valuation from someone who sells here.',
      cta: 'valuation',
    },
  },
];

export const findLandingPage = (slug) => LANDING_PAGES.find((pg) => pg.slug === slug);
